import React, { useEffect, useMemo, useState } from "react";
import { DefaultButton, MessageBar, MessageBarType, Stack, Text, mergeStyleSets } from "@fluentui/react";
import { getMonth, getYear, parseISO } from "date-fns";
import StreakMonth, { CELL_COLORS } from "./StreakMonth";
import { STATUS, STATUS_LABELS, buildDayMap } from "./streakUtils";

const classes = mergeStyleSets({
    months: {
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(220px, 280px))",
        gap: 16,
    },
    legendSwatch: {
        display: "inline-block",
        width: 16,
        height: 16,
        borderRadius: 3,
        boxSizing: "border-box",
        verticalAlign: "middle",
        marginRight: 6,
    },
});

const LEGEND = [STATUS.COMPLETED, STATUS.PATCHED, STATUS.MISSED, STATUS.IN_PROGRESS];

const StreakCalendar = ({ history, onSelectPeriod }) => {
    const dayMap = useMemo(() => buildDayMap(history.periods), [history.periods]);
    const firstDate = parseISO(history.periods[0].startDate);
    const lastDate = parseISO(history.today);
    const minYear = getYear(firstDate);
    const maxYear = getYear(lastDate);

    const [year, setYear] = useState(maxYear);
    useEffect(() => setYear(maxYear), [history.memberId, maxYear]);

    const firstMonth = year === minYear ? getMonth(firstDate) : 0;
    const lastMonth = year === maxYear ? getMonth(lastDate) : 11;
    const months = [];
    for (let month = firstMonth; month <= lastMonth; month++) {
        months.push(new Date(year, month, 1));
    }

    return (
        <Stack tokens={{ childrenGap: 16 }}>
            <Stack horizontal horizontalAlign="start" verticalAlign="center" tokens={{ childrenGap: 16 }}>
                <DefaultButton
                    text={`${year - 1}`}
                    iconProps={{ iconName: "ChevronLeft" }}
                    onClick={() => setYear(year - 1)}
                    style={{ visibility: year > minYear ? "visible" : "hidden" }}
                />
                <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>{year}</Text>
                <DefaultButton
                    text={`${year + 1}`}
                    menuIconProps={{ iconName: "ChevronRight" }}
                    onClick={() => setYear(year + 1)}
                    style={{ visibility: year < maxYear ? "visible" : "hidden" }}
                />
            </Stack>

            <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
                {LEGEND.map((status) => (
                    <span key={status}>
                        <span className={classes.legendSwatch} style={CELL_COLORS[status]} />
                        {STATUS_LABELS[status]}
                    </span>
                ))}
            </Stack>

            <MessageBar messageBarType={MessageBarType.info} styles={{ root: { maxWidth: 872 } }}>
                Click on a red {history.streakCadence} to fix the member's streak. Click on a purple {history.streakCadence} to see who patched it.
            </MessageBar>

            <div className={classes.months}>
                {months.map((monthStart) => (
                    <StreakMonth
                        key={monthStart.toISOString()}
                        monthStart={monthStart}
                        dayMap={dayMap}
                        today={history.today}
                        onSelectPeriod={onSelectPeriod}
                    />
                ))}
            </div>
        </Stack>
    );
};

export default StreakCalendar;
